import {CanvasEditor} from "./CanvasEditor";
import {ProcessCanvas} from "./ProcessCanvas";
import {ProcessElement} from "./ProcessElement";
import {Tool} from "./Tool";
import {addSelectionChangeListener} from "./ManipulationEditor";
import {ProcessNodeProps} from "./ProcessNodeProps";
import {ProcessEdgeProps} from "./ProcessEdgeProps";
import {ProcessLaneProps} from "./ProcessLaneProps";
import {NODE_REGISTRY} from "./NodeRegistry";

export class ProcessEditor extends CanvasEditor {
	/**
	 * @param {object} model - model of process definition
	 */
	constructor(canvasId, model) {
		super(canvasId);
		this.isProcessEditor = true;	// mark this object as process editor to enable parent-tree lookup (without circular dependency)
		/** @type {PModel} */
		this.model = model;
		/** @type {[ProcessNodeEditor]} */
		this.nodeEditors = [];
		/** @type {[EdgeEditor]} */
		this.edgeEditors = [];
		/** @type {[SwimlaneEditor]} */
		this.laneEditors = [];

		this.props = false;
		this.propsEditor = null;
	}

	init() {
		this.cleanUpModel();
		super.init();
		this.buildEditors();
		addSelectionChangeListener(this.selectionChanged.bind(this));
	}

	selectionChanged() {
		if (this.props) {
			let selected = this.countSelected();
			const container = $("#props");
			container.empty();
			this.propsEditor = null;
			if (selected.total === 1) {
				let selectedEditor, fieldPath, obj, member;
				if (selected.nodes === 1) {
					for (let ed of this.nodeEditors) {
						if (ed.selected) {
							selectedEditor = ed;
							obj = selectedEditor;
							member = fieldPath = "node";
							break;
						}
					}
					this.propsEditor = new ProcessNodeProps(this, fieldPath, obj, member);
				} else if (selected.edges === 1) {
					for (let ed of this.edgeEditors) {
						if (ed.selected) {
							selectedEditor = ed;
							obj = selectedEditor.con;
							member = fieldPath = "edge";
							break;
						}
					}
					this.propsEditor = new ProcessEdgeProps(this, fieldPath, obj, member);
				} else {
					for (let ed of this.laneEditors) {
						if (ed.selected) {
							selectedEditor = ed;
							obj = selectedEditor;
							member = fieldPath = "lane";
							break;
						}
					}
					this.propsEditor = new ProcessLaneProps(this, fieldPath, obj, member);
				}
				container.append(this.propsEditor.ui);
			} else if (selected.total === 0) {
				// Process properties
				const modelContainer = { model: this.model };
				// REMOVED LEGACY FORM IMPLEMENTATION
				// this.propsEditor = new ProcessProps(this, "model", modelContainer, "model");
				// container.append(this.propsEditor.ui);
			}
		}
	}

	/**
	 * User changed type of the node
	 * @param {PNode} node
	 */
	nodeTypeChanged(node) {
		let oldEditor;
		for (let i = 0; i < this.nodeEditors.length; i++) {
			let ed = this.nodeEditors[i];
			if (ed.node === node) {
				// Found
				const newEditor = ProcessElement.newNodeEditor(this, node, this.model.chart[node.id]);
				newEditor.selected = ed.selected;
				this.nodeEditors.splice(i, 1, newEditor);
				this.selectionChanged();
				this.update();
				return;
			}
		}
	}

	/**
	 * @return {{edges:{number}, nodes:{number}, lanes:{number}}}
	 */
	countSelected() {
		const result = {};
		let count = 0;
		for (let ed of this.edgeEditors) {
			if (ed.selected) count++;
		}
		result.edges = count;
		count = 0;
		for (let ed of this.nodeEditors) {
			if (ed.selected) count++;
		}
		result.nodes = count;
		count = 0;
		for (let ed of this.laneEditors) {
			if (ed.selected) count++;
		}
		result.lanes = count;
		result.total = result.edges + result.nodes + result.lanes;
		return result;
	}

	createCanvas() {
		return new ProcessCanvas(this);
	}

	toolChanged() {
		this.canvas.unselectAll();
	}

	static estimateDimensionsFromWindow() {
		const w = $(window);
		let width = w.width() - 200 - 3*8;	// menu width and 3x space
		let height = w.height() - 240;
		if (width < 512) width = 512;
		if (height < 384) width = 384;
		return { width: width, height: height };
	}

	/**
	 * Clean-up model, correct or add missing visual coordinates
	 */
	cleanUpModel() {
		const m = this.model;
		if (!m.nodes) m.nodes = [];
		if (!m.connections) m.connections = [];
		if (!m.params) m.params = [];
		if (!m.chart) m.chart = {};
		const ch = m.chart;
		const defaults = { w: 60, h: 60, x: 100, y: 100 };

		for (let n of m.nodes) {
			let coords = ch[n.id];
			if (typeof coords !== "object") {
				ch[n.id] = coords = {};
			}
			ProcessElement.cleanUpCoords(n, coords, defaults);
		}
	}

	buildEditors() {
		for (let n of this.model.nodes) {
			const ed = ProcessElement.newNodeEditor(this, n, this.model.chart[n.id]);
			if (ed !== null) this.nodeEditors.push(ed);
		}

		for (let c of this.model.connections) {
			const fromCoords = this.model.chart[c.from];
			const toCoords = this.model.chart[c.to];
			if (fromCoords && toCoords) {
				const ed = ProcessElement.newEdgeEditor(this, c, fromCoords, toCoords);
				this.edgeEditors.push(ed);
			}
		}

		if (this.model.lanes) {
			for (let l of this.model.lanes) {
				if (!this.model.chartsl.hasOwnProperty(l.id)) this.model.chartsl[l.id] = { t:100, l:100, w:512, h:128};
				const ed = ProcessElement.newLaneEditor(this, l, this.model.chartsl[l.id]);
				if (ed !== null) this.laneEditors.push(ed);
			}
		}
	}

	/**
	 * Find connection or return null if not found
	 * @param {string} fromId
	 * @param {string} toId
	 * @return {PConnection|null}
	 */
	findConnection(fromId, toId) {
		for (let con of this.model.connections) {
			if (con.from === fromId && con.to === toId) return con;
		}
		return null;
	}

	/**
	 * @return {PConnection[]}
	 */
	findOutgoingConnections(fromId) {
		const cons = [];
		for (let con of this.model.connections) {
			if (con.from === fromId) cons.push(con);
		}
		return cons;
	}

	/**
	 * @return {PConnection[]}
	 */
	findIncommingConnections(toId) {
		const cons = [];
		for (let con of this.model.connections) {
			if (con.to === toId) cons.push(con);
		}
		return cons;
	}

	/**
	 * Find edge editor or return null if not found
	 * @param {string} id
	 * @return {EdgeEditor|null}
	 */
	findEdgeEditor(fromId, toId) {
		for (let ee of this.edgeEditors) {
			if (ee.node.id === id) return ee;
		}
		return null;
	}

	/**
	 * Find node or return null if not found
	 * @param {string} id
	 * @return {PNode|null}
	 */
	findNode(id) {
		for (let n of this.model.nodes) {
			if (n.id === id) return n;
		}
		return null;
	}
	/**
	 * Find node editor or return null if not found
	 * @param {string} id
	 * @return {ProcessNodeEditor|null}
	 */
	findNodeEditor(id) {
		for (let ne of this.nodeEditors) {
			if (ne.node.id === id) return ne;
		}
		return null;
	}

	newObject(type, x, y) {
		if (type === "swimlane") return this.newLane(x, y);
		else return this.newNodeObject(type, x, y);
	}

	newLane(x, y) {
		const lane = {};
		lane.id = this.randomId("lane-", (id)=>this.laneIdUsed(id));
		lane.class = ProcessElement.getDefaultClassForLane();
		if (!this.model.lanes) this.model.lanes = [];
		this.model.lanes.push(lane);

		// Coords
		let pos = Tool.normalize(x, y, this.canvas);
		const defaults = { w: 512, h: 128, l: pos.x-256, t: pos.y-64 };
		if (!this.model.chartsl) this.model.chartsl = {};
		this.model.chartsl[lane.id] = {};
		ProcessElement.cleanUpLaneCoords(lane, this.model.chartsl[lane.id], defaults);
		// Create editor
		const ed = ProcessElement.newLaneEditor(this, lane, this.model.chartsl[lane.id]);
		if (ed !== null) {
			this.laneEditors.push(ed);
			this.update();
			return ed;
		}
		return null;
	}

	newNodeObject(type, x, y) {
		/** @type {PNode} */
		const node = {};
		node.id = this.randomId("node-", (id)=>this.nodeIdUsed(id));
		node.class = ProcessElement.getDefaultClassForNodeType(type);
		this.model.nodes.push(node);

		// Coords
		let pos = Tool.normalize(x, y, this.canvas);
		const defaults = { w: 60, h: 60, x: pos.x, y: pos.y };
		this.model.chart[node.id] = {};
		ProcessElement.cleanUpCoords(node, this.model.chart[node.id], defaults);
		// Create editor
		const ed = ProcessElement.newNodeEditor(this, node, this.model.chart[node.id]);
		if (ed !== null) {
			this.nodeEditors.push(ed);
			this.update();
			return ed;
		}
		return null;
	}

	newEdge(fromId, toId) {

		// Check if already connected
		if (this.findConnection(fromId, toId) !== null || this.findConnection(toId, fromId) !== null) {
			return null;
		}
		const fromNode = this.findNode(fromId);
		const toNode = this.findNode(toId);

		const fromMeta = NODE_REGISTRY.get(fromNode.class);
		const toMeta = NODE_REGISTRY.get(toNode.class);

		const outs = this.findOutgoingConnections(fromId);
		const ins = this.findIncommingConnections(toId);

		let disconnectFrom = [];
		let disconnectTo = [];

		if (fromMeta.maxOut !== null) {
			// outgoing count is limited
			while (fromMeta.maxOut <= outs.length) {
				disconnectFrom = disconnectFrom.concat(outs.splice(outs.length-1, 1));
			}
		}
		if (toMeta.maxIn !== null) {
			// incomming count is limited
			while (toMeta.maxIn <= ins.length) {
				disconnectTo = disconnectTo.concat(ins.splice(ins.length-1, 1));
			}
		}

		for (let d of disconnectFrom) this.deleteEdge(d.from, d.to);
		for (let d of disconnectTo) this.deleteEdge(d.from, d.to);

		/** @type {PConnection} */
		const c = {};
		c.edge = this.generateNewEdge(fromId, outs);
		c.from = fromId;
		c.to = toId;
		this.model.connections.push(c);

		const fromCoords = this.model.chart[c.from];
		const toCoords = this.model.chart[c.to];
		if (fromCoords && toCoords) {
			const ed = ProcessElement.newEdgeEditor(this, c, fromCoords, toCoords);
			this.edgeEditors.push(ed);
			this.update();
			return ed;
		}
		return null;
	}

	generateNewEdge(fromId, currentConnections) {
		const ed = this.findNodeEditor(fromId);
		return ed.generateOutgoingEdge(currentConnections);
	}


	/**
	 * @param {string} prefix
	 * @param {function} existenceTest
	 * @return {string}
	 */
	randomId(prefix, existenceTest) {
		let id;
		let idx = 1;
		do {
			id = prefix + idx;
			idx++;
		} while (existenceTest(id));
		return id;
	}

	nodeIdUsed(id) {
		for (let node of this.model.nodes) {
			if (id === node.id) return true;
		}
		return false;
	}

	laneIdUsed(id) {
		if (this.model.lanes) {
			for (let sl of this.model.lanes) {
				if (id === sl.id) return true;
			}
		}
		return false;
	}

	delete(e) {

		const invalidatedNodes = [];
		for (let nodeEditor of this.nodeEditors) {
			if (nodeEditor.selected) invalidatedNodes.push(nodeEditor.node.id);
		}

		this.deleteSelectedEdges(invalidatedNodes);
		this.deleteSelectedNodes();
		this.deleteSelectedLanes();
		this.update();
	}

	deleteEdge(fromId, toId) {
		const toDelete = [];
		const toKeep = [];
		for (let edgeEditor of this.edgeEditors) {
			if (edgeEditor.con.from === fromId && edgeEditor.con.to === toId) toDelete.push(edgeEditor);
			else toKeep.push(edgeEditor);
		}
		this.deleteInternal(toDelete, toKeep);
	}

	deleteSelectedEdges(ivalidatedNodeIds) {
		const toDelete = [];
		const toKeep = [];
		for (let edgeEditor of this.edgeEditors) {
			if (edgeEditor.selected
				|| ivalidatedNodeIds.indexOf(edgeEditor.con.from)>= 0
				|| ivalidatedNodeIds.indexOf(edgeEditor.con.to)>= 0) toDelete.push(edgeEditor);
			else toKeep.push(edgeEditor);
		}
		this.deleteInternal(toDelete, toKeep);
	}

	deleteInternal(toDelete, toKeep) {
		if (toDelete.length > 0) {
			for (let ed of toDelete) {
				const delCon = ed.con;
				for (let i = 0; i < this.model.connections.length; i++) {
					const con = this.model.connections[i];
					if (delCon === con) {
						this.model.connections.splice(i, 1);
						const nodeCoords = this.model.chart[delCon.from];
						if (nodeCoords.out) {
							delete nodeCoords.out[delCon.edge.id];
						}
						break;
					}
				}
			}
			this.edgeEditors = toKeep;
		}
	}

	deleteSelectedNodes() {
		const toDelete = [];
		const toKeep = [];
		for (let nodeEditor of this.nodeEditors) {
			if (nodeEditor.selected) toDelete.push(nodeEditor);
			else toKeep.push(nodeEditor);
		}
		if (toDelete.length > 0) {
			for (let ed of toDelete) {
				const delNode = ed.node;
				for (let i = 0; i < this.model.nodes.length; i++) {
					const node = this.model.nodes[i];
					if (delNode === node) {
						this.model.nodes.splice(i, 1);
						break;
					}
				}
				if (this.model.chart) {
					if (this.model.chart.hasOwnProperty(delNode.id)) {
						delete this.model.chartsl[delNode.id];
					}
				}
			}
			this.nodeEditors = toKeep;
		}
	}

	deleteSelectedLanes() {
		const toDelete = [];
		const toKeep = [];
		for (let laneEditor of this.laneEditors) {
			if (laneEditor.selected) toDelete.push(laneEditor);
			else toKeep.push(laneEditor);
		}
		if (toDelete.length > 0) {
			for (let ed of toDelete) {
				const delLane = ed.lane;
				for (let i = 0; i < this.model.lanes.length; i++) {
					const lane = this.model.lanes[i];
					if (delLane === lane) {
						this.model.lanes.splice(i, 1);
						break;
					}
				}
				if (this.model.chartsl) {
					if (this.model.chartsl.hasOwnProperty(delLane.id)) {
						delete this.model.chartsl[delLane.id];
					}
				}
			}
			this.laneEditors = toKeep;
		}
	}

	/**
	 * @param {boolean} [show] - show or hide the props
	 */
	displayProps(show) {
		if (typeof show === "undefined") {
			// return the state
			return this.props;
		} else if (typeof show === "boolean") {
			// set the state
			const change = this.props !== show;
			this.props = show;
			if (change) {
				const propsElm = document.getElementById("props");
				const dim = ProcessEditor.estimateDimensionsFromWindow();
				const ppw = this.propsPanelWidth;
				if (this.props) {
					this.initSize(dim.width - ppw, dim.height);
					propsElm.style.display = "block";
					propsElm.style.width = ppw+"px";
					propsElm.style.height = dim.height+"px";
					this.selectionChanged();
				} else {
					this.initSize(dim.width, dim.height);
					propsElm.style.display = "none";
					this.propsEditor = null;
				}
			}
		}
	}

	get propsPanelWidth() {
		return 300;
	}
}
